import React, { useEffect } from 'react';
import objectPath from 'object-path';
import { arrayOf, shape, string, func, bool } from 'prop-types';
import commonErrors from '@shared_modules/common-properties/errors';
import { Page } from '../../pages/pi1-1QAAT/components';
import rejectedBorrowerImg from './images/rejected-borrower.svg';
import loanAppInProgressImg from './images/loan-app-in-progress.svg';
import defaultErrorImg from './images/default-error.svg';
import { heapTrackEvent } from '@shared_modules/baui-heap-tracking';

import './ContinueApp.scss';

const errorMap = {
  RECENT_LC_LOAN: {
    title: 'Thanks for returning to LendingClub',
    header: (
      <>
        We are sorry, but we are unable to proceed with your application at this time because you recently got a loan
        with us. After 30 days has passed from your last loan issued, you can try again from your account{' '}
        <a href="/apply/personal/identity">here</a>. Your information has been saved.
      </>
    ),
    image: rejectedBorrowerImg,
    submitlabel: 'Go to My Account',
    className: 'continue-app__img--rejected-borrower',
  },
  REJECTED_BORROWER: {
    title: 'Thanks for returning to LendingClub',
    header: 'You’ve applied within the last 30 days. See the status of your current application.',
    image: rejectedBorrowerImg,
    submitlabel: 'Got It',
    className: 'continue-app__img--rejected-borrower',
  },
  LOAN_APP_IN_PROGRESS: {
    title: 'Great news! We found your application',
    header: 'Continue or edit your current application to see your offer.',
    image: loanAppInProgressImg,
    submitlabel: 'Got It',
    className: 'continue-app__img--loan-app-in-progress',
  },
  CAPTCHA_ERROR_HANDLER: {
    title: 'Your information has been saved. Let’s check your rate!',
    header: 'Click “Check Your Rate” to submit your loan application.',
    image: defaultErrorImg,
    submitlabel: 'Check Your Rate',
    className: 'continue-app__img--default',
  },
  DEFAULT: {
    title: 'We hit a snag, but your information was saved. Let’s try again',
    header: 'Try resubmitting the form or if you continue to have problems please try again later.',
    image: defaultErrorImg,
    submitlabel: 'Try Again',
    className: 'continue-app__img--default',
  },
};

const ContinueApp = ({
  errors,
  saveValues,
  nextLocation,
  saveAndGoToNextLocation,
  shouldUseSaveAndGoToNextLocation,
  setIsShowingContinueApp,
  productType,
}) => {
  const foundError = errors.err && errors.err.find(err => err.error);
  const errorCode = foundError && foundError.error;
  const errorPage = objectPath.get(errorMap, `${errorCode}`, errorMap.DEFAULT);
  const isCaptchaError = ['CAPTCHA_ERROR_HANDLER'].includes(errorCode);
  const currentProductType = productType || 'PL';

  const getHeapTrackProps = actionName => ({
    ERROR_CODE: errorCode,
    CONTENT_TYPE: isCaptchaError ? 'valid_captcha' : 'error',
    ACTION: actionName,
    ERROR_TYPE: errorCode,
    PRODUCT_TYPE: currentProductType,
    ERROR_MESSAGE_SHOWN: `${errorPage.title} ${headerText}`,
  });

  const isSpecialError = [
    'LOAN_APP_IN_PROGRESS',
    'REJECTED_BORROWER',
    'RECENT_LC_LOAN',
    'CAPTCHA_ERROR_HANDLER',
  ].includes(errorCode);

  const headerText = !isSpecialError ? objectPath.get(commonErrors, `${errorCode}`) : errorPage.header;
  useEffect(() => {
    heapTrackEvent('Originations_Error_Page_Tracking', getHeapTrackProps('view'));
    setIsShowingContinueApp(true);
    return () => setIsShowingContinueApp(false);
  }, []);

  const submit = e => {
    if (e) e.preventDefault();
    if (!shouldUseSaveAndGoToNextLocation) saveValues();
    heapTrackEvent('Originations_Error_Page_Tracking', getHeapTrackProps('cta_click'));
    if (foundError.error === 'RECENT_LC_LOAN') window.location.assign('/account/services/dashboard');
    else if (isSpecialError && !isCaptchaError) window.location.reload(true);
    else shouldUseSaveAndGoToNextLocation ? saveAndGoToNextLocation() : nextLocation();
  };

  return (
    <Page
      title={errorPage.title}
      header={headerText}
      name="continue-app"
      submitHandler={submit}
      submitLabel={errorPage.submitlabel}
      contentClassName="continue-app__center"
    >
      <div className="continue-app__content">
        <img src={errorPage.image} alt="apply" className={errorPage.className} />
      </div>
    </Page>
  );
};

ContinueApp.propTypes = {
  errors: shape({
    err: arrayOf(
      shape({
        error: string,
        message: string,
      })
    ),
  }),
  nextLocation: func,
  saveValues: func,
  saveAndGoToNextLocation: func,
  shouldUseSaveAndGoToNextLocation: bool,
  setIsShowingContinueApp: func,
  productType: string,
};

ContinueApp.defaultProps = {
  errors: {},
  saveValues: () => {},
  nextLocation: () => {},
  saveAndGoToNextLocation: () => {},
  shouldUseSaveAndGoToNextLocation: false,
  setIsShowingContinueApp: () => {},
  productType: '',
};

export default ContinueApp;

